import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { 
  ClipboardCheck, 
  LayoutDashboard, 
  Users, 
  Building2, 
  FileText, 
  LogOut,
  ChevronDown,
  Menu,
  X,
  User,
  Settings,
  FolderKanban,
  HelpCircle,
  Lightbulb,
  AlertTriangle,
  Cog,
  Trophy,
  BarChart3
} from 'lucide-react';
import { useState } from 'react';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getNavItems = () => {
    if (user?.role === 'admin') {
      return [
        { path: '/admin', label: 'Tableau de bord', icon: LayoutDashboard },
        { path: '/admin/projects', label: 'Projets', icon: FolderKanban },
        { path: '/admin/users', label: 'Utilisateurs', icon: Users },
        { path: '/admin/companies', label: 'Entreprises', icon: Building2 },
        { path: '/admin/interventions', label: 'Interventions', icon: FileText },
        { path: '/admin/final-reports', label: 'Rapports Finaux', icon: Trophy },
        { path: '/stats/digitalization', label: 'Digitalisation', icon: BarChart3 },
        { path: '/admin/reports', label: 'Signalements', icon: AlertTriangle },
        { path: '/admin/configuration', label: 'Configuration', icon: Cog },
      ];
    }
    if (user?.role === 'consultant') {
      return [
        { path: '/consultant', label: 'Tableau de bord', icon: LayoutDashboard },
        { path: '/consultant/interventions', label: 'Mes interventions', icon: FileText },
        { path: '/consultant/companies', label: 'Entreprises', icon: Building2 },
      ];
    }
    if (user?.role === 'controleur') {
      return [
        { path: '/controleur', label: 'Tableau de bord', icon: LayoutDashboard },
        { path: '/stats/digitalization', label: 'Digitalisation', icon: BarChart3 },
      ];
    }
    return [
      { path: '/pme', label: 'Tableau de bord', icon: LayoutDashboard },
      { path: '/pme/pending', label: 'Validations en attente', icon: FileText },
      { path: '/pme/history', label: 'Historique', icon: ClipboardCheck },
    ];
  };

  const navItems = getNavItems();

  const isActive = (path) => {
    if (path === `/${user?.role}` || path === '/admin' || path === '/pme' || path === '/controleur') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrateur',
      consultant: 'Consultant',
      sme: 'PME',
      controleur: 'Contrôleur'
    };
    return labels[role] || role;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-white/80 border-b border-slate-200/50">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button 
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img 
                src="https://customer-assets.emergentagent.com/job_consult-tracker-14/artifacts/zvf1f82s_logo-ccitogo.png" 
                alt="CCI Togo" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold text-slate-900 font-['Plus_Jakarta_Sans'] hidden sm:block">
                CCI Togo
              </span>
            </Link>
          </div>

          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                  <Avatar className="h-8 w-8">
                    {user?.profile_photo ? (
                      <AvatarImage src={user.profile_photo} alt={user.name} />
                    ) : null}
                    <AvatarFallback className="bg-slate-900 text-white text-sm">
                      {user?.name?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium text-slate-700">{user?.name}</span>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{user?.name}</span>
                    <span className="text-xs text-slate-500 font-normal">{user?.email}</span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-slate-500 text-xs uppercase tracking-wider" disabled>
                  {getRoleLabel(user?.role)}
                </DropdownMenuItem>
                {user?.company_name && (
                  <DropdownMenuItem className="text-slate-600" disabled>
                    <Building2 className="w-4 h-4 mr-2" />
                    {user.company_name}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-link">
                  <User className="w-4 h-4 mr-2" />
                  Mon profil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="settings-link">
                  <Settings className="w-4 h-4 mr-2" />
                  Paramètres
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 
          transform transition-transform duration-200 ease-in-out
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          pt-20 lg:pt-0
        `}>
          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium
                    transition-colors duration-150
                    ${active 
                      ? 'bg-slate-900 text-white' 
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
            
            {/* Documentation & Development links - always visible */}
            <div className="pt-4 mt-4 border-t border-slate-200 space-y-1">
              <Link
                to="/documentation"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="nav-documentation"
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium
                  transition-colors duration-150
                  ${location.pathname === '/documentation'
                    ? 'bg-slate-900 text-white' 
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }
                `}
              >
                <HelpCircle className="w-5 h-5" />
                Documentation
              </Link>
              
              {/* Development link - for consultants, sme, admin */}
              {['admin', 'consultant', 'sme'].includes(user?.role) && (
                <Link
                  to="/development"
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="nav-development"
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium
                    transition-colors duration-150
                    ${location.pathname === '/development'
                      ? 'bg-slate-900 text-white' 
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                    }
                  `}
                >
                  <Lightbulb className="w-5 h-5" />
                  Développement
                </Link>
              )}
            </div>
          </nav>
        </aside>

        {/* Mobile overlay */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Main content */}
        <main className="flex-1 p-6 lg:p-8 min-h-[calc(100vh-73px)]">
          {children}
        </main>
      </div>
    </div>
  );
};
